/**
 * Plugin for enabling Android immersive mode
 * This will only run on the Android native app
 */

export default defineNuxtPlugin({
  name: 'android-immersive',
  setup(nuxtApp) {
    // Only run in client-side code
    if (!process.client) return;
    
    // Use the application logger instead of console
    const { debug, info, warn, error, logErrorCaught } = useLogger();
    
    // Logger helper with our component prefix
    const logDebug = (message: string, details?: any) => debug(`[AndroidImmersive] ${message}`, details);
    const logInfo = (message: string, details?: any) => info(`[AndroidImmersive] ${message}`, details);
    const logWarn = (message: string, details?: any) => warn(`[AndroidImmersive] ${message}`, details);
    const logError = (message: string, details?: any) => error(`[AndroidImmersive] ${message}`, details);
    
    // Log the setup start
    logInfo('Plugin setup started');

    // Function to check if running in native Android app
    const isNativeAndroid = () => {
      const capacitor = (window as any).Capacitor;
      const isNative = typeof capacitor !== 'undefined' && 
             typeof capacitor.isNativePlatform === 'function' && 
             capacitor.isNativePlatform() === true &&
             typeof capacitor.getPlatform === 'function' &&
             capacitor.getPlatform() === 'android';
      
      logDebug(`isNativeAndroid check: ${isNative}`);
      return isNative;
    };

    // Flag to track if we're in an Android app
    const inAndroidApp = isNativeAndroid();
    
    if (!inAndroidApp) {
      // Not running in Android app, don't do anything
      logInfo('Not running in Android app, plugin inactive');
      return;
    }
    
    logInfo('Running in Android native app, plugin active');
    
    // Add flag to track if we SHOULD be in immersive mode based on route
    // This helps prevent unwanted re-enabling of immersive mode
    let shouldBeImmersive = false;

    // Force init Capacitor plugins to ensure they're available
    try {
      const capacitor = (window as any).Capacitor;
      if (capacitor && capacitor.Plugins) {
        logDebug('Attempting to initialize Capacitor plugins');
        // Force access SystemUI plugin to make sure it's loaded
        if (!capacitor.Plugins.SystemUI) {
          logDebug('SystemUI plugin not found in initial check, attempting to register');
          // Sometimes plugins need to be accessed once to be registered
          // This is a workaround for some Capacitor plugin initialization issues
        }
      }
    } catch (err) {
      logErrorCaught('Failed to initialize plugins', 'android-immersive-plugin', err);
    }

    // Wait a short time for plugins to initialize
    setTimeout(() => {
      const capacitor = (window as any).Capacitor;
      if (capacitor?.Plugins) {
        logDebug(`Available plugins after initialization: ${Object.keys(capacitor.Plugins).join(', ')}`);
      }
    }, 1000);

    // Enable immersive mode on Android
    const enableImmersiveMode = async () => {
      logInfo('enableImmersiveMode called');
      try {
        const capacitor = (window as any).Capacitor;
        
        // Detailed debugging of available plugins
        if (capacitor?.Plugins) {
          const pluginNames = Object.keys(capacitor.Plugins);
          logDebug(`All available Capacitor plugins: ${pluginNames.join(', ')}`);
          
          // Check for any plugin that might be the SystemUI plugin with different casing
          const systemUiPlugin = pluginNames.find(
            plugin => plugin.toLowerCase() === 'systemui' || 
                     plugin.toLowerCase() === 'system_ui' || 
                     plugin.toLowerCase() === 'system-ui'
          );
          
          if (systemUiPlugin) {
            logDebug(`Found SystemUI plugin with name: ${systemUiPlugin}`);
          }
        } else {
          logWarn('No Capacitor.Plugins object found!');
        }
        
        // Check available plugins by name
        const hasSystemUI = !!capacitor?.Plugins?.SystemUI;
        // We'll skip StatusBar as it's not properly implemented on Android
        // const hasStatusBar = !!capacitor?.Plugins?.StatusBar;
        
        logDebug(`Available plugins: SystemUI=${hasSystemUI}, AndroidFullScreen=${!!(window as any).AndroidFullScreen}`);
        
        // SKIP StatusBar calls - they're failing with "not implemented on android"
        // if (hasStatusBar) {
        //  logDebug('Calling StatusBar.hide()');
        //  await capacitor.Plugins.StatusBar.hide();
        // }
        
        // Try to use the SystemUI plugin with exact case match first
        let systemUISuccess = false;
        if (hasSystemUI) {
          logDebug('Calling SystemUI.hideSystemUI() with exact case match');
          try {
            const result = await capacitor.Plugins.SystemUI.hideSystemUI();
            logDebug(`SystemUI.hideSystemUI result: ${JSON.stringify(result)}`);
            systemUISuccess = true;
          } catch (error) {
            logErrorCaught('Error calling SystemUI.hideSystemUI', 'android-immersive-plugin', error);
          }
        }
        
        // If exact case match failed, try to find any case variation
        if (!systemUISuccess && capacitor?.Plugins) {
          logDebug('Exact case match failed, searching for case-insensitive matches');
          const pluginNames = Object.keys(capacitor.Plugins);
          
          for (const pluginName of pluginNames) {
            if (pluginName.toLowerCase() === 'systemui' || 
                pluginName.toLowerCase() === 'system_ui' || 
                pluginName.toLowerCase() === 'system-ui') {
              try {
                logDebug(`Trying plugin with name: ${pluginName}`);
                const result = await capacitor.Plugins[pluginName].hideSystemUI();
                logDebug(`${pluginName}.hideSystemUI result: ${JSON.stringify(result)}`);
                systemUISuccess = true;
                break;
              } catch (e) {
                logWarn(`Error with ${pluginName}`, e);
              }
            }
          }
        }
        
        // If still not successful, try native MainActivity as a last resort
        if (!systemUISuccess) {
          logDebug('Plugin approach failed, trying direct enableImmersiveMode if available');
          try {
            // This is a last resort attempt to call directly
            if (capacitor?.getPlatform && capacitor.getPlatform() === 'android') {
              logDebug('Using direct MainActivity enableImmersiveMode call');
              // Try to access MainActivity on WebView through bridge
              const androidApp = (window as any).androidApp;
              if (androidApp && typeof androidApp.enableImmersiveMode === 'function') {
                await androidApp.enableImmersiveMode();
                logDebug('Direct call to MainActivity.enableImmersiveMode successful');
                systemUISuccess = true;
              }
            }
          } catch (e) {
            logErrorCaught('Direct enableImmersiveMode error', 'android-immersive-plugin', e);
          }
        }
        
        // If using Cordova plugins like AndroidFullScreen
        if (!systemUISuccess && (window as any).AndroidFullScreen) {
          logDebug('Calling androidFullScreen.immersiveMode()');
          await (window as any).AndroidFullScreen.immersiveMode();
          systemUISuccess = true;
        }
        
        logInfo(`Immersive mode ${systemUISuccess ? 'enabled successfully' : 'FAILED to enable'}`);
        return systemUISuccess;
      } catch (err) {
        logErrorCaught('Failed to enable immersive mode', 'android-immersive-plugin', err);
        return false;
      }
    };

    // Disable immersive mode
    const disableImmersiveMode = async () => {
      logInfo('disableImmersiveMode called');
      try {
        const capacitor = (window as any).Capacitor;
        const hasSystemUI = !!capacitor?.Plugins?.SystemUI;
        // We'll skip StatusBar calls - they're failing with "not implemented on android"
        // const hasStatusBar = !!capacitor?.Plugins?.StatusBar;
        
        // Skip StatusBar calls as they're failing
        // let statusBarSuccess = false;
        // if (hasStatusBar) {
        //   logDebug('Calling StatusBar.show()');
        //   try {
        //     await capacitor.Plugins.StatusBar.show();
        //     statusBarSuccess = true;
        //     logDebug('StatusBar.show() successful');
        //   } catch (error) {
        //     logErrorCaught('Error calling StatusBar.show()', 'android-immersive-plugin', error);
        //   }
        // }

        // Try to use the SystemUI plugin
        let systemUISuccess = false;
        if (hasSystemUI) {
          logDebug('Calling SystemUI.showSystemUI()');
          try {
            const result = await capacitor.Plugins.SystemUI.showSystemUI();
            logDebug(`SystemUI.showSystemUI result: ${JSON.stringify(result)}`);
            systemUISuccess = true;
          } catch (error) {
            logErrorCaught('Error calling SystemUI.showSystemUI', 'android-immersive-plugin', error);
          }
        }
        
        // If exact case match failed, try to find any case variation
        if (!systemUISuccess && capacitor?.Plugins) {
          const pluginNames = Object.keys(capacitor.Plugins);
          
          for (const pluginName of pluginNames) {
            if (pluginName.toLowerCase() === 'systemui' || 
                pluginName.toLowerCase() === 'system_ui' || 
                pluginName.toLowerCase() === 'system-ui') {
              try {
                logDebug(`Trying plugin with name: ${pluginName}`);
                await capacitor.Plugins[pluginName].showSystemUI();
                systemUISuccess = true;
                break;
              } catch (e) {
                logWarn(`Error with ${pluginName}`, e);
              }
            }
          }
        }
        
        // Skip StatusBar retry - it's not working
        // Skip this block:
        // if (!statusBarSuccess && capacitor?.Plugins?.StatusBar) {
        //   logDebug('First StatusBar.show() failed, trying again with options');
        //   try {
        //     // Try with explicit options
        //     await capacitor.Plugins.StatusBar.setStyle({ style: "LIGHT" });
        //     await capacitor.Plugins.StatusBar.setBackgroundColor({ color: "#000000" });
        //     await capacitor.Plugins.StatusBar.show();
        //     statusBarSuccess = true;
        //     logDebug('StatusBar show with options successful');
        //   } catch (error) {
        //     logErrorCaught('Error in second StatusBar.show() attempt', 'android-immersive-plugin', error);
        //   }
        // }

        // NEW: Add multiple timed events to help layout recalculation
        logDebug('Scheduling layout recalculation events');
        
        // Schedule multiple resize events to ensure layout gets recalculated
        // as the system UI may take some time to fully appear
        const scheduleResizeEvent = (delayMs: number) => {
          setTimeout(() => {
            logDebug(`Firing resize event after ${delayMs}ms delay`);
            try {
              // Dispatch resize event to trigger layout recalculation
              window.dispatchEvent(new Event('resize'));
              
              // Also try to notify Vue components through custom event
              window.dispatchEvent(new CustomEvent('system-ui-visible'));
            } catch (e) {
              logWarn('Error dispatching resize event', e);
            }
          }, delayMs);
        };
        
        // Schedule multiple resize events at different intervals
        // This increases chances that at least one will fire after system UI is fully visible
        scheduleResizeEvent(100);  // Quick resize
        scheduleResizeEvent(300);  // Medium delay
        scheduleResizeEvent(800);  // Longer delay for slower devices
        
        // Use just systemUISuccess since we're skipping StatusBar calls
        logInfo(`Immersive mode ${systemUISuccess ? 'disabled successfully' : 'FAILED to disable'}`);
        return systemUISuccess;
      } catch (err) {
        logErrorCaught('Failed to disable immersive mode', 'android-immersive-plugin', err);
        return false;
      }
    };

    // Watch for route changes to apply immersive mode only on the clock page
    const route = useRoute();
    
    logInfo('Setting up route watcher for immersive mode');
    let currentPath = route.path;
    
    // Function to handle route changes
    const handleRouteChange = async (newPath: string) => {
      logDebug(`Route changed from ${currentPath} to: ${newPath}`);
      
      // Check if we're navigating to the clock page - this is high priority
      const isNavigatingToClock = newPath.includes('/user/clock');
      
      // Check if we're navigating away from the clock page
      const isNavigatingFromClock = currentPath.includes('/user/clock') && !newPath.includes('/user/clock');
      
      // Store the new path for next comparison
      currentPath = newPath;
      
      // Update our state tracking flag
      shouldBeImmersive = isNavigatingToClock;
      
      // Handle Clock page with highest priority
      if (isNavigatingToClock) {
        logInfo('Navigation to clock page detected, enabling immersive mode');
        
        // Immediate attempt to enable immersive mode
        const initialResult = await enableImmersiveMode();
        logInfo(`Immersive mode enable result: ${initialResult}`);
        
        // Just one retry with a delay to ensure it gets applied
        if (!initialResult) {
          setTimeout(async () => {
            // Only proceed if we should still be immersive
            if (!shouldBeImmersive) {
              logInfo('Cancelling retry attempt because we left clock page');
              return;
            }
            
            logInfo('Retrying immersive mode enable (300ms delay)');
            const retryResult = await enableImmersiveMode();
            logInfo(`Retry enable result: ${retryResult}`);
          }, 300);
        }
        
      } else if (isNavigatingFromClock) {
        logInfo('Navigation away from clock page detected, disabling immersive mode');
        
        // Set our tracking flag first to prevent any pending enable attempts
        shouldBeImmersive = false;
        
        // Immediate attempt to disable
        const result = await disableImmersiveMode();
        logInfo(`Immersive mode disable result: ${result}`);
        
        // Just one retry with a delay to ensure it gets applied
        if (!result) {
          setTimeout(async () => {
            logInfo('Retrying immersive mode disable (300ms delay)');
            const retryResult = await disableImmersiveMode();
            logInfo(`Retry disable result: ${retryResult}`);
          }, 300);
        }
      } else {
        // For non-clock pages, ensure immersive mode is disabled
        logDebug('Non-clock page detected, ensuring immersive mode is disabled');
        shouldBeImmersive = false;
        disableImmersiveMode();
      }
    };
    
    // Watch for route changes with immediate execution
    // The immediate flag is important - it ensures we run on first render
    watch(() => route.path, handleRouteChange, { immediate: true });
    
    // Add a global event listener for page transitions
    if (process.client) {
      window.addEventListener('popstate', () => {
        logDebug('Detected popstate event (user used back button)');
        if (currentPath.includes('/user/clock')) {
          logInfo('User navigated away from clock page using back button, disabling immersive mode');
          disableImmersiveMode();
        }
      });
    }

    // Handle app resume events to reapply immersive mode when needed
    const capacitor = (window as any).Capacitor;
    if (capacitor?.app) {
      logDebug('Setting up app resume listener');
      capacitor.app.addListener('resume', async () => {
        logInfo('App resumed from background');
        // Simple check - if on clock page and should be immersive, enable
        // Otherwise, make sure it's disabled
        if (route.path.includes('/user/clock') && shouldBeImmersive) {
          logInfo('Currently on clock page, re-enabling immersive mode');
          await enableImmersiveMode();
        } else if (!shouldBeImmersive) {
          logInfo('Not on clock page after resume, ensuring immersive mode is disabled');
          await disableImmersiveMode();
        }
      });
    }

    // Simplified focus listener
    if (process.client) {
      window.addEventListener('focus', async () => {
        logInfo('Window regained focus');
        // Same logic as resume - simple check based on current page
        if (route.path.includes('/user/clock') && shouldBeImmersive) {
          logInfo('On clock page with focus, ensuring immersive mode');
          await enableImmersiveMode();
        } else if (!route.path.includes('/user/clock')) {
          logInfo('Not on clock page with focus, ensuring normal mode');
          shouldBeImmersive = false;
          await disableImmersiveMode();
        }
      });
    }

    logInfo('Android immersive plugin setup complete');
    
    // Expose functions for components to use
    return {
      provide: {
        androidImmersive: {
          enable: enableImmersiveMode,
          disable: disableImmersiveMode,
          isAndroidApp: inAndroidApp
        }
      }
    };
  }
}); 